import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  Typography,
  CircularProgress,
} from '@mui/material'
import dayjs from 'dayjs'
import { useAtomValue } from 'jotai'
import React, { useState } from 'react'
import { useNavigate } from 'react-router'

import { useUsers } from '../../contexts/UserContext'
import { instructorsAtom } from '../../domains/user/AtomUsers'
import PrivateSessionService from '../../services/PrivateSessionService'
import useBar from '../common/Bar'

export default function PrivateSessionTable() {
  const navigate = useNavigate()
  const instructors = useAtomValue(instructorsAtom)
  const { initInstructors } = useUsers()
  const bar = useBar()
  const [isLoading, setIsLoading] = useState(false)
  const [sessions, setSessions] = useState([])

  // Table state
  const [orderBy, setOrderBy] = useState('startTime')
  const [order, setOrder] = useState('desc')
  const [instructorFilter, setInstructorFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')

  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedSession, setSelectedSession] = useState(null)

  // Get all sessions
  const fetchSessions = async () => {
    setIsLoading(true)
    try {
      const filters = {
        size: 999,
      }
      const response = await PrivateSessionService.getPrivateSessions(filters)
      setSessions(response)
    } catch (error) {
      console.error('Failed to fetch sessions:', error)
      bar.error(error.message || 'Failed to fetch sessions')
    } finally {
      setIsLoading(false)
    }
  }

  // 初始化加载数据
  React.useEffect(() => {
    const init = async () => {
      await Promise.all([fetchSessions(), initInstructors()])
    }
    init()
  }, [])

  // Handle sort
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  // Sort function
  const sortData = (data) => {
    if (data === undefined || data.length === 0) return []

    return data.sort((a, b) => {
      let aValue = a[orderBy]
      let bValue = b[orderBy]

      // Special handling for dates
      if (orderBy === 'startTime') {
        aValue = dayjs(a.startTime)
        bValue = dayjs(b.startTime)
      }

      if (order === 'asc') {
        return aValue < bValue ? -1 : 1
      } else {
        return bValue < aValue ? -1 : 1
      }
    })
  }

  // Filter function
  const filterData = (data) => {
    if (data === undefined) return []

    return data.filter((session) => {
      const matchesInstructor = instructorFilter === 'all' || session.instructorId === instructorFilter
      const searchTerms = searchQuery.toLowerCase().trim().split(/\s+/)

      const instructor = instructors.find((inst) => inst.value === session.instructorId)
      const instructorName = instructor ? `${instructor.firstName} ${instructor.lastName}` : ''

      const matchesSearch = searchTerms.every((term) => {
        return (
          instructorName.toLowerCase().includes(term) ||
          session.skiResort.toLowerCase().includes(term) ||
          session.status.toLowerCase().includes(term) ||
          session.instructorId.toLowerCase().includes(term)
        )
      })

      return matchesInstructor && matchesSearch
    })
  }

  // Handle delete dialog
  const handleDeleteClick = (session) => {
    setSelectedSession(session)
    setDeleteDialogOpen(true)
  }

  // Handle session deletion
  const handleDelete = async () => {
    if (!selectedSession) return

    try {
      await PrivateSessionService.deletePrivateSession(selectedSession.resourceId)
      bar.success('Session deleted successfully')
      setDeleteDialogOpen(false)
      await fetchSessions() // Reload data
    } catch (error) {
      console.error('Failed to delete session:', error)
      bar.error(error.message || 'Failed to delete session')
    } finally {
      setSelectedSession(null)
    }
  }

  // Get instructor name
  const getInstructorName = (instructorId) => {
    const instructor = instructors.find((inst) => inst.value === instructorId)
    return instructor ? `${instructor.firstName} ${instructor.lastName}` : 'Unknown Instructor'
  }

  // Process data with sorting and filtering
  const processedData = sortData(filterData(sessions))

  const handleUserClick = (userId) => {
    navigate(`/users/${userId}`)
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 300 }}
          placeholder="Search by instructor, location, or status"
          helperText="Multiple keywords supported (space separated)"
        />
        <FormControl size="small" sx={{ width: 200 }}>
          <InputLabel>Instructor Filter</InputLabel>
          <Select value={instructorFilter} onChange={(e) => setInstructorFilter(e.target.value)} label="Instructor Filter">
            <MenuItem value="all">All Instructors</MenuItem>
            {instructors.map((instructor) => (
              <MenuItem key={instructor.value} value={instructor.value}>
                {instructor.firstName} {instructor.lastName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table sx={{ tableLayout: 'fixed' }} size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '15%' }}>
                <TableSortLabel active={orderBy === 'instructorId'} direction={orderBy === 'instructorId' ? order : 'asc'} onClick={() => handleRequestSort('instructorId')}>
                  Instructor ID
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '15%' }}>
                <TableSortLabel active={orderBy === 'instructorName'} direction={orderBy === 'instructorName' ? order : 'asc'} onClick={() => handleRequestSort('instructorName')}>
                  Instructor
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '15%' }}>Student Name</TableCell>
              <TableCell sx={{ width: '15%' }}>
                <TableSortLabel active={orderBy === 'startTime'} direction={orderBy === 'startTime' ? order : 'asc'} onClick={() => handleRequestSort('startTime')}>
                  Start Time
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '12%' }}>
                <TableSortLabel active={orderBy === 'skiResort'} direction={orderBy === 'skiResort' ? order : 'asc'} onClick={() => handleRequestSort('skiResort')}>
                  Location
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '13%' }}>
                <TableSortLabel active={orderBy === 'status'} direction={orderBy === 'status' ? order : 'asc'} onClick={() => handleRequestSort('status')}>
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '15%' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {processedData.map((session) => (
              <TableRow key={session.resourceId} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                <TableCell>{session.instructorId}</TableCell>
                <TableCell>
                  <Box
                    component="span"
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                    onClick={() => handleUserClick(session.instructorId)}
                  >
                    {getInstructorName(session.instructorId)}
                  </Box>
                </TableCell>
                <TableCell>
                  {session.participantIds?.length > 0 ? (
                    <Stack direction="row" spacing={0.5}>
                      {session.participantIds.map((studentId) => (
                        <Chip
                          key={studentId}
                          label={studentId}
                          size="small"
                          sx={{
                            height: 20,
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                          onClick={() => handleUserClick(studentId)}
                        />
                      ))}
                    </Stack>
                  ) : (
                    'No students'
                  )}
                </TableCell>
                <TableCell>{dayjs(session.startTime).format('YYYY-MM-DD HH:mm')}</TableCell>
                <TableCell>{session.skiResort}</TableCell>
                <TableCell>
                  <Chip
                    label={session.status}
                    size="small"
                    color={session.status === 'BOOKED' ? 'primary' : session.status === 'COMPLETED' ? 'success' : session.status === 'CANCELLED' ? 'error' : 'default'}
                    sx={{ height: 20 }}
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => {}} sx={{ p: 0.5 }}>
                        <VisibilityIcon sx={{ fontSize: '1.1rem' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Session">
                      <IconButton size="small" onClick={() => {}} sx={{ p: 0.5 }}>
                        <EditIcon sx={{ fontSize: '1.1rem' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Session">
                      <IconButton size="small" onClick={() => handleDeleteClick(session)} sx={{ p: 0.5 }}>
                        <DeleteIcon sx={{ fontSize: '1.1rem' }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        disablePortal={false}
        keepMounted
      >
        <DialogTitle id="delete-dialog-title">Delete Session</DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">Are you sure you want to delete this session? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit" autoFocus>
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleDelete()
              setDeleteDialogOpen(false)
            }}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
